import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Column,
  Heading,
  Tag,
  Button,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Toggle,
  SkeletonText,
  SkeletonPlaceholder,
  ToastNotification,
  NumberInput,
  Checkbox,
} from '@carbon/react';
import {
  Settings,
  Save,
  Reset,
  Notification,
  Security,
  User,
  Building,
  Education,
  Money,
  Calendar,
  Checkmark,
  Warning,
  Information,
  Email,
  Globe,
  Download,
  Upload,
  Edit,
  Star,
} from '@carbon/icons-react';
import nemoIcon from '../../assets/nemo.png';
import tigerIcon from '../../assets/tiger.png';
import kingIcon from '../../assets/king.png';
import { trainingsService } from '../../services/trainings.service';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      siteName: 'PHINLEX',
      siteDescription: 'Répertoire dédié à la formation pharmaceutique en Algérie et à travers l\'Afrique',
      defaultLanguage: 'fr',
      timezone: 'Africa/Algiers',
      maintenanceMode: false,
    },
    notifications: {
      emailNotifications: true,
      adminAlerts: true,
      userRegistrationAlerts: true,
      paymentAlerts: true,
      trainingApprovalAlerts: true,
    },
    security: {
      twoFactorAuth: true,
      passwordMinLength: 8,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      requireEmailVerification: true,
      enableCaptcha: true,
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      smtpUsername: 'admin@phinlex.dz',
      smtpPassword: '',
      fromEmail: 'noreply@phinlex.dz',
      fromName: 'PHINLEX',
    },
    payment: {
      currency: 'DZD',
      taxRate: 19,
      commissionRate: 5,
      enableStripe: true,
      enablePayPal: false,
      enableBankTransfer: true,
    },
    training: {
      autoApproval: false,
      requireDocumentation: true,
      maxFileSize: 10,
      allowedFileTypes: ['pdf', 'doc', 'docx', 'jpg', 'png'],
      enableReviews: true,
      enableRatings: true,
    },
  });

  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);

  const [editingPlan, setEditingPlan] = useState<number | null>(null);
  const [notification, setNotification] = useState<{
    title: string;
    subtitle: string;
    kind: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Load subscription plans from database
      const plansData = await trainingsService.getSubscriptionPlans();
      setSubscriptionPlans(plansData);

      // Simulate other settings API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoading(false);
    } catch (error) {
      setNotification({
        title: t('common.error', 'Error'),
        subtitle: t('admin.settings.errors.load', 'Unable to load settings'),
        kind: 'error',
      });
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNotification({
        title: t('common.success', 'Success'),
        subtitle: t('admin.settings.messages.saved', 'Settings saved successfully'),
        kind: 'success',
      });
      setLoading(false);
    } catch (error) {
      setNotification({
        title: t('common.error', 'Error'),
        subtitle: t('admin.settings.errors.save', 'Unable to save settings'),
        kind: 'error',
      });
      setLoading(false);
    }
  };

  const handleResetSettings = () => {
    setNotification({
      title: t('common.info', 'Information'),
      subtitle: t('admin.settings.messages.reset', 'Settings reset to default values'),
      kind: 'info',
    });
  };

  const updateSetting = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [key]: value,
      },
    }));
  };

  const updatePlan = (planId: number, field: string, value: any) => {
    setSubscriptionPlans(prev =>
      prev.map(plan =>
        plan.id === planId
          ? { ...plan, [field]: value }
          : plan
      )
    );
  };

  const savePlanChanges = async (planId: number) => {
    try {
      setLoading(true);

      // Find the plan to update
      const planToUpdate = subscriptionPlans.find(plan => plan.id === planId);
      if (!planToUpdate) {
        throw new Error('Plan not found');
      }

      // Update plan in database
      await trainingsService.updateSubscriptionPlan(planId, {
        price: planToUpdate.price,
        listing_limit: planToUpdate.listing_limit,
        title: planToUpdate.title,
        duration: planToUpdate.duration,
        isActive: planToUpdate.isActive
      });

      setEditingPlan(null);
      setNotification({
        title: 'Succès',
        subtitle: 'Plan mis à jour avec succès',
        kind: 'success',
      });
      setLoading(false);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de sauvegarder le plan',
        kind: 'error',
      });
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', label: t('admin.settings.tabs.general', 'General'), icon: Settings },
    { id: 'notifications', label: t('admin.settings.tabs.notifications', 'Notifications'), icon: Notification },
    { id: 'security', label: t('admin.settings.tabs.security', 'Security'), icon: Security },
    { id: 'email', label: t('admin.settings.tabs.email', 'Email'), icon: Email },
    { id: 'payment', label: t('admin.settings.tabs.payments', 'Payments'), icon: Money },
    { id: 'training', label: t('admin.settings.tabs.training', 'Training'), icon: Education },
    { id: 'subscription-plans', label: t('admin.settings.tabs.subscriptionPlans', 'Subscription plans'), icon: Star },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonText heading width="60%" />
        <SkeletonPlaceholder className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading className="text-3xl font-bold text-gray-900 mb-2">
            {t('admin.settings.header.title', 'System settings')}
          </Heading>
          <p className="text-gray-600">
            {t('admin.settings.header.subtitle', 'Configure PHINLEX platform settings')}
          </p>
        </div>
        <div className="flex space-x-3">
          <Button kind="tertiary" renderIcon={Reset} onClick={handleResetSettings}>
            {t('admin.settings.actions.reset', 'Reset')}
          </Button>
          <Button kind="primary" renderIcon={Save} onClick={handleSaveSettings}>
            {t('admin.settings.actions.save', 'Save')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex space-x-1 border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${activeTab === tab.id
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.general.title', 'General settings')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <TextInput
                  id="site-name"
                  labelText={t('admin.settings.general.siteName', 'Site name')}
                  value={settings.general.siteName}
                  onChange={(e) => updateSetting('general', 'siteName', e.target.value)}
                  className="mb-4"
                />
              </Column>
              <Column lg={6} md={12}>
                <Select
                  id="default-language"
                  labelText={t('admin.settings.general.defaultLanguage', 'Default language')}
                  value={settings.general.defaultLanguage}
                  onChange={(e) => updateSetting('general', 'defaultLanguage', e.target.value)}
                  className="mb-4"
                >
                  <SelectItem value="fr" text="Français" />
                  <SelectItem value="ar" text="العربية" />
                  <SelectItem value="en" text="English" />
                </Select>
              </Column>
              <Column lg={6} md={12}>
                <Select
                  id="timezone"
                  labelText={t('admin.settings.general.timezone', 'Timezone')}
                  value={settings.general.timezone}
                  onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
                  className="mb-4"
                >
                  <SelectItem value="Africa/Algiers" text="Algérie (UTC+1)" />
                  <SelectItem value="Europe/Paris" text="Paris (UTC+1/+2)" />
                  <SelectItem value="UTC" text="UTC" />
                </Select>
              </Column>
              <Column lg={6} md={12}>
                <div className="mb-4">
                  <Toggle
                    id="maintenance-mode"
                    labelText={t('admin.settings.general.maintenanceMode', 'Maintenance mode')}
                    toggled={settings.general.maintenanceMode}
                    onChange={(e: any) => updateSetting('general', 'maintenanceMode', e.target.checked)}
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    {t('admin.settings.general.maintenanceNote', 'Enable maintenance mode for users')}
                  </p>
                </div>
              </Column>
              <Column lg={12} md={12}>
                <TextArea
                  id="site-description"
                  labelText={t('admin.settings.general.siteDescription', 'Site description')}
                  value={settings.general.siteDescription}
                  onChange={(e) => updateSetting('general', 'siteDescription', e.target.value)}
                  rows={3}
                />
              </Column>
            </Grid>
          </div>
        </div>
      )}

      {/* Notifications Settings */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.notifications.title', 'Notification settings')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="email-notifications"
                    labelText={t('admin.settings.notifications.emailNotifications', 'Email notifications')}
                    toggled={settings.notifications.emailNotifications}
                    onChange={(e: any) => updateSetting('notifications', 'emailNotifications', e.target.checked)}
                  />
                  <Toggle
                    id="admin-alerts"
                    labelText={t('admin.settings.notifications.adminAlerts', 'Admin alerts')}
                    toggled={settings.notifications.adminAlerts}
                    onChange={(e: any) => updateSetting('notifications', 'adminAlerts', e.target.checked)}
                  />
                </div>
              </Column>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="user-registration-alerts"
                    labelText={t('admin.settings.notifications.userRegistrationAlerts', 'Registration alerts')}
                    toggled={settings.notifications.userRegistrationAlerts}
                    onChange={(e: any) => updateSetting('notifications', 'userRegistrationAlerts', e.target.checked)}
                  />
                  <Toggle
                    id="payment-alerts"
                    labelText={t('admin.settings.notifications.paymentAlerts', 'Payment alerts')}
                    toggled={settings.notifications.paymentAlerts}
                    onChange={(e: any) => updateSetting('notifications', 'paymentAlerts', e.target.checked)}
                  />
                  <Toggle
                    id="training-approval-alerts"
                    labelText={t('admin.settings.notifications.trainingApprovalAlerts', 'Training approval alerts')}
                    toggled={settings.notifications.trainingApprovalAlerts}
                    onChange={(e: any) => updateSetting('notifications', 'trainingApprovalAlerts', e.target.checked)}
                  />
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.security.title', 'Security settings')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="two-factor-auth"
                    labelText={t('admin.settings.security.twoFactorAuth', 'Two-factor authentication')}
                    toggled={settings.security.twoFactorAuth}
                    onChange={(e: any) => updateSetting('security', 'twoFactorAuth', e.target.checked)}
                  />
                  <NumberInput
                    id="password-min-length"
                    label={t('admin.settings.security.passwordMinLength', 'Minimum password length')}
                    value={settings.security.passwordMinLength}
                    onChange={(e: any) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
                    min={6}
                    max={20}
                  />
                  <NumberInput
                    id="session-timeout"
                    label={t('admin.settings.security.sessionTimeout', 'Session timeout (minutes)')}
                    value={settings.security.sessionTimeout}
                    onChange={(e: any) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
                    min={5}
                    max={1440}
                  />
                </div>
              </Column>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <NumberInput
                    id="max-login-attempts"
                    label={t('admin.settings.security.maxLoginAttempts', 'Max login attempts')}
                    value={settings.security.maxLoginAttempts}
                    onChange={(e: any) => updateSetting('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    min={3}
                    max={10}
                  />
                  <Toggle
                    id="require-email-verification"
                    labelText={t('admin.settings.security.requireEmailVerification', 'Require email verification')}
                    toggled={settings.security.requireEmailVerification}
                    onChange={(e: any) => updateSetting('security', 'requireEmailVerification', e.target.checked)}
                  />
                  <Toggle
                    id="enable-captcha"
                    labelText={t('admin.settings.security.enableCaptcha', 'Enable CAPTCHA')}
                    toggled={settings.security.enableCaptcha}
                    onChange={(e: any) => updateSetting('security', 'enableCaptcha', e.target.checked)}
                  />
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.email.title', 'Email configuration')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <TextInput
                  id="smtp-host"
                  labelText={t('admin.settings.email.smtpHost', 'SMTP host')}
                  value={settings.email.smtpHost}
                  onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
                  className="mb-4"
                />
                <NumberInput
                  id="smtp-port"
                  label={t('admin.settings.email.smtpPort', 'SMTP port')}
                  value={settings.email.smtpPort}
                  onChange={(e: any) => updateSetting('email', 'smtpPort', parseInt(e.target.value))}
                  className="mb-4"
                />
                <TextInput
                  id="smtp-username"
                  labelText={t('admin.settings.email.smtpUsername', 'SMTP username')}
                  value={settings.email.smtpUsername}
                  onChange={(e) => updateSetting('email', 'smtpUsername', e.target.value)}
                  className="mb-4"
                />
              </Column>
              <Column lg={6} md={12}>
                <TextInput
                  id="smtp-password"
                  labelText={t('admin.settings.email.smtpPassword', 'SMTP password')}
                  type="password"
                  value={settings.email.smtpPassword}
                  onChange={(e) => updateSetting('email', 'smtpPassword', e.target.value)}
                  className="mb-4"
                />
                <TextInput
                  id="from-email"
                  labelText={t('admin.settings.email.fromEmail', 'From email')}
                  value={settings.email.fromEmail}
                  onChange={(e) => updateSetting('email', 'fromEmail', e.target.value)}
                  className="mb-4"
                />
                <TextInput
                  id="from-name"
                  labelText={t('admin.settings.email.fromName', 'From name')}
                  value={settings.email.fromName}
                  onChange={(e) => updateSetting('email', 'fromName', e.target.value)}
                  className="mb-4"
                />
              </Column>
            </Grid>
            <div className="mt-4">
              <Button kind="tertiary" renderIcon={Email}>
                {t('admin.settings.email.testConfig', 'Test email configuration')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Settings */}
      {activeTab === 'payment' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.payment.title', 'Payment settings')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <Select
                  id="currency"
                  labelText={t('admin.settings.payment.currency', 'Currency')}
                  value={settings.payment.currency}
                  onChange={(e) => updateSetting('payment', 'currency', e.target.value)}
                  className="mb-4"
                >
                  <SelectItem value="DZD" text="Dinar algérien (DZD)" />
                  <SelectItem value="EUR" text="Euro (EUR)" />
                  <SelectItem value="USD" text="Dollar américain (USD)" />
                </Select>
                <NumberInput
                  id="tax-rate"
                  label={t('admin.settings.payment.taxRate', 'VAT rate (%)')}
                  value={settings.payment.taxRate}
                  onChange={(e: any) => updateSetting('payment', 'taxRate', parseFloat(e.target.value))}
                  className="mb-4"
                  min={0}
                  max={100}
                />
                <NumberInput
                  id="commission-rate"
                  label={t('admin.settings.payment.commissionRate', 'Commission rate (%)')}
                  value={settings.payment.commissionRate}
                  onChange={(e: any) => updateSetting('payment', 'commissionRate', parseFloat(e.target.value))}
                  className="mb-4"
                  min={0}
                  max={50}
                />
              </Column>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="enable-stripe"
                    labelText={t('admin.settings.payment.enableStripe', 'Enable Stripe')}
                    toggled={settings.payment.enableStripe}
                    onChange={(e: any) => updateSetting('payment', 'enableStripe', e.target.checked)}
                  />
                  <Toggle
                    id="enable-paypal"
                    labelText={t('admin.settings.payment.enablePayPal', 'Enable PayPal')}
                    toggled={settings.payment.enablePayPal}
                    onChange={(e: any) => updateSetting('payment', 'enablePayPal', e.target.checked)}
                  />
                  <Toggle
                    id="enable-bank-transfer"
                    labelText={t('admin.settings.payment.enableBankTransfer', 'Enable bank transfer')}
                    toggled={settings.payment.enableBankTransfer}
                    onChange={(e: any) => updateSetting('payment', 'enableBankTransfer', e.target.checked)}
                  />
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      )}

      {/* Training Settings */}
      {activeTab === 'training' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.training.title', 'Training settings')}</Heading>
          </div>
          <div className="p-6">
            <Grid>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="auto-approval"
                    labelText={t('admin.settings.training.autoApproval', 'Auto approval')}
                    toggled={settings.training.autoApproval}
                    onChange={(e: any) => updateSetting('training', 'autoApproval', e.target.checked)}
                  />
                  <Toggle
                    id="require-documentation"
                    labelText={t('admin.settings.training.requireDocumentation', 'Documentation required')}
                    toggled={settings.training.requireDocumentation}
                    onChange={(e: any) => updateSetting('training', 'requireDocumentation', e.target.checked)}
                  />
                  <NumberInput
                    id="max-file-size"
                    label={t('admin.settings.training.maxFileSize', 'Max file size (MB)')}
                    value={settings.training.maxFileSize}
                    onChange={(e: any) => updateSetting('training', 'maxFileSize', parseInt(e.target.value))}
                    min={1}
                    max={100}
                  />
                </div>
              </Column>
              <Column lg={6} md={12}>
                <div className="space-y-4">
                  <Toggle
                    id="enable-reviews"
                    labelText={t('admin.settings.training.enableReviews', 'Enable reviews')}
                    toggled={settings.training.enableReviews}
                    onChange={(e: any) => updateSetting('training', 'enableReviews', e.target.checked)}
                  />
                  <Toggle
                    id="enable-ratings"
                    labelText={t('admin.settings.training.enableRatings', 'Enable ratings')}
                    toggled={settings.training.enableRatings}
                    onChange={(e: any) => updateSetting('training', 'enableRatings', e.target.checked)}
                  />
                  <div>
                    <p className="text-sm font-medium mb-2">{t('admin.settings.training.allowedFileTypes', 'Allowed file types')}</p>
                    <div className="space-y-2">
                      {['pdf', 'doc', 'docx', 'jpg', 'png'].map((type) => (
                        <Checkbox
                          key={type}
                          id={`file-type-${type}`}
                          labelText={type.toUpperCase()}
                          checked={settings.training.allowedFileTypes.includes(type)}
                          onChange={(e) => {
                            const currentTypes = settings.training.allowedFileTypes;
                            const newTypes = e.target.checked
                              ? [...currentTypes, type]
                              : currentTypes.filter(t => t !== type);
                            updateSetting('training', 'allowedFileTypes', newTypes);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Column>
            </Grid>
          </div>
        </div>
      )}

      {/* Subscription Plans Settings */}
      {activeTab === 'subscription-plans' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <Heading className="text-xl font-semibold">{t('admin.settings.subscription.title', 'Subscription plans management')}</Heading>
            <p className="text-gray-600 mt-2">
              {t('admin.settings.subscription.subtitle', 'Edit prices and training limits for each subscription plan')}
            </p>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              </div>
            ) : subscriptionPlans.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{t('admin.settings.subscription.noPlans', 'No subscription plans found')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {subscriptionPlans.map((plan, index) => {
                  // Define plan colors and icons based on index using brand colors
                  const getPlanStyle = (index: number) => {
                    switch (index) {
                      case 0: // Nemo Pack
                        return {
                          bgColor: 'bg-brand-primary/10',
                          borderColor: 'border-brand-primary/30',
                          selectedBg: 'bg-brand-primary/20',
                          selectedBorder: 'border-brand-primary',
                          icon: nemoIcon,
                          iconAlt: 'Nemo'
                        };
                      case 1: // Tiger Pack
                        return {
                          bgColor: 'bg-brand-secondary/10',
                          borderColor: 'border-brand-secondary/30',
                          selectedBg: 'bg-brand-secondary/20',
                          selectedBorder: 'border-brand-secondary',
                          icon: tigerIcon,
                          iconAlt: 'Tiger'
                        };
                      case 2: // King Pack
                        return {
                          bgColor: 'bg-brand-accent/10',
                          borderColor: 'border-brand-accent/30',
                          selectedBg: 'bg-brand-accent/20',
                          selectedBorder: 'border-brand-accent',
                          icon: kingIcon,
                          iconAlt: 'King'
                        };
                      default:
                        return {
                          bgColor: 'bg-gray-50',
                          borderColor: 'border-gray-200',
                          selectedBg: 'bg-gray-100',
                          selectedBorder: 'border-gray-400',
                          icon: Star,
                          iconAlt: 'Star'
                        };
                    }
                  };

                  const planStyle = getPlanStyle(index);

                  return (
                    <div
                      key={plan.id}
                      className={`relative border-2 rounded-lg p-3 transition-all duration-200 ${editingPlan === plan.id
                        ? `${planStyle.selectedBg} ${planStyle.selectedBorder} shadow-lg`
                        : `${planStyle.bgColor} ${planStyle.borderColor} hover:shadow-md`
                        }`}
                    >
                      <div className="text-center mb-3">
                        {/* Icon */}
                        <div className="flex justify-center mb-2">
                          {typeof planStyle.icon === 'string' ? (
                            <img
                              src={planStyle.icon}
                              alt={planStyle.iconAlt}
                              className="h-12 w-12 object-contain"
                            />
                          ) : (
                            <planStyle.icon className="h-12 w-12 text-brand-primary" />
                          )}
                        </div>

                        {/* Plan Name */}
                        <h3 className="text-base font-bold text-gray-900 mb-2">{plan.title}</h3>

                        {/* Price */}
                        <div className="mb-3">
                          <div className="text-lg font-bold text-gray-900">{plan.price.toLocaleString()} DZD</div>
                          <div className="text-xs text-gray-600">{t('subscriptions.plans.perMonth', 'per month')}</div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('admin.settings.subscription.priceDzd', 'Price (DZD)')}
                          </label>
                          {editingPlan === plan.id ? (
                            <NumberInput
                              id={`price-${plan.id}`}
                              value={plan.price}
                              onChange={(e: any) => updatePlan(plan.id, 'price', parseInt(e.target.value))}
                              min={0}
                              step={100}
                              size="sm"
                            />
                          ) : (
                            <div className="p-2 bg-gray-50 rounded text-sm font-semibold">
                              {plan.price.toLocaleString()} DZD
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('admin.settings.subscription.trainingLimit', 'Limit trainings')}
                          </label>
                          {editingPlan === plan.id ? (
                            <NumberInput
                              id={`limit-${plan.id}`}
                              value={plan.listing_limit || 0}
                              onChange={(e: any) => updatePlan(plan.id, 'listing_limit', parseInt(e.target.value) || null)}
                              min={0}
                              placeholder="0 = Illimité"
                              size="sm"
                            />
                          ) : (
                            <div className="p-2 bg-gray-50 rounded text-sm font-semibold">
                              {plan.listing_limit ? `${plan.listing_limit}` : '∞'}
                            </div>
                          )}
                        </div>

                        <div className="flex justify-center pt-2">
                          <div className="flex space-x-2">
                            {editingPlan === plan.id ? (
                              <>
                                <button
                                  onClick={() => setEditingPlan(null)}
                                  className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-full shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200"
                                  title={t('common.cancel', 'Cancel')}
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => savePlanChanges(plan.id)}
                                  className="flex items-center justify-center w-10 h-10 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200"
                                  title={t('common.save', 'Save')}
                                >
                                  <Save className="h-5 w-5" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setEditingPlan(plan.id)}
                                className="flex items-center justify-center w-10 h-10 bg-brand-secondary hover:bg-brand-secondary/90 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200"
                                title="Modifier"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Information className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">{t('common.info', 'Information')}</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('admin.settings.subscription.infoNote', 'Changes to subscription plans take effect immediately for new subscriptions. Existing subscriptions are not affected.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <ToastNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          onClose={() => setNotification(null)}
          timeout={5000}
        />
      )}
    </div>
  );
};

export default SettingsPage;
